import { SwitchCase } from '@toss/react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { ArrowRightIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import type { HintData } from '@/api/gen/_model/hintData';
import type { Item } from '@/api/gen/_model/item';
import type { QuizData } from '@/api/gen/_model/quizData';
import { QuizCard, type QuizType } from '@/shared/components/QuizCardV2';
import { QuizEssayAnswer } from '@/shared/components/QuizCardV2/QuizEssayAnswer';
import { type HintType } from '@/shared/components/QuizCardV2/types';
import { prepareTiptapHTML, StaticRichTextRenderer } from '@/shared/features/rich-text';
import { type QuizHint } from '@/types';

/**
 * QuizItemData - QuizDetail 페이지에서 표시할 퀴즈 항목 데이터 타입
 * API의 QuizData를 기반으로 하되, id를 필수로 하고 imageUrl을 optional로 재정의
 */
export type QuizItemData = Omit<QuizData, 'imageUrl'> & {
  id: string;
  imageUrl?: string;
};

/**
 * QuizItemCard - QuizDetail 페이지용 읽기 전용 퀴즈 카드
 * Story 1.1의 QuizCardV2 아토믹 컴포넌트 사용
 *
 * @description
 * 아토믹 QuizCard 컴포넌트를 사용하여 퀴즈 문제를 읽기 전용 형식으로 표시합니다.
 * 세 가지 퀴즈 유형을 모두 지원: 객관식, OX, 주관식.
 *
 * @example
 * <QuizItemCard data={quizData} index={0} />
 */

interface QuizItemCardProps {
  data: QuizItemData;
  index: number;
}
export const QuizItemCard = ({
  data,
  index
}: QuizItemCardProps) => {
  const {
    t
  } = useTranslation(['common', 'build']);

  // 공통 데이터 추출
  const questionNumber = `${t('quizItem.question.title')} ${index + 1}`;
  const imageUrl = data.imageUrl;
  const hint = data.hint;
  return <QuizCard.Root className="flex-shrink-0" data-sentry-element="unknown" data-sentry-component="QuizItemCard" data-sentry-source-file="QuizItemCard.tsx">
      <QuizCard.Header data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      <QuizCard.Content data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx">
        <div className="flex items-center gap-sm">
          <span className="text-heading-sm-bold text-default">
            {questionNumber}
          </span>
          <QuizCard.TypeBadge type={data.type as QuizType} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />
        </div>
        <SwitchCase value={data.type} caseBy={{
        multiple: <MultipleChoiceContent data={data} imageUrl={imageUrl} hint={hint} />,
        ox: <OXChoiceContent data={data} imageUrl={imageUrl} hint={hint} />,
        subjective: <SubjectiveContent data={data} imageUrl={imageUrl} hint={hint} />,
        essay: <EssayContent data={data} imageUrl={imageUrl} hint={hint} />,
        match: <MatchContent data={data} imageUrl={imageUrl} hint={hint} />
      }} data-sentry-element="SwitchCase" data-sentry-source-file="QuizItemCard.tsx" />
      </QuizCard.Content>
    </QuizCard.Root>;
};

/**
 * MultipleChoiceContent - 객관식 퀴즈 콘텐츠 렌더링
 */
const MultipleChoiceContent = ({
  data,
  imageUrl,
  hint
}: {
  data: QuizItemData;
  imageUrl?: string;
  hint?: HintData;
}) => {
  const multipleData = data.multipleData;
  if (!multipleData) return null;
  const questionText = multipleData.questionRichText || multipleData.question || '';
  const choices = multipleData.choiceListV2 || [];
  const answerList = multipleData.answerList?.map(Number) || [];
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <QuizCard.MultipleChoice.Container data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx">
        {choices.map((choice, idx) => {
        const choiceIndex = idx;
        const isAnswer = answerList.includes(choiceIndex);
        return <QuizCard.MultipleChoice.Item key={idx} text={choice.text || ''} imageUrl={choice.imageUrl} isAnswer={isAnswer} choiceNumber={choiceIndex} />;
      })}
      </QuizCard.MultipleChoice.Container>

      {hint?.value && <QuizCard.Hint type={hint.type as QuizHint['type']} value={hint.value} />}
    </>;
};

/**
 * OXChoiceContent - O/X 퀴즈 콘텐츠 렌더링
 */
const OXChoiceContent = ({
  data,
  imageUrl,
  hint
}: {
  data: QuizItemData;
  imageUrl?: string;
  hint?: HintData;
}) => {
  const oxData = data.oxData;
  if (!oxData) return null;
  const questionText = oxData.questionRichText || oxData.question || '';
  const answer = oxData.answer?.toLowerCase() as 'o' | 'x';
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <QuizCard.OXChoice.Container answer={answer} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {hint?.value && <QuizCard.Hint type={hint.type as HintType} value={hint.value} />}
    </>;
};

/**
 * SubjectiveContent - 주관식 퀴즈 콘텐츠 렌더링
 */
const SubjectiveContent = ({
  data,
  imageUrl,
  hint
}: {
  data: QuizItemData;
  imageUrl?: string;
  hint?: HintData;
}) => {
  const subjectiveData = data.subjectiveData;
  if (!subjectiveData) return null;
  const questionText = subjectiveData.questionRichText || subjectiveData.question || '';
  const answerList = subjectiveData.answerList || [];
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <QuizCard.SubjectiveAnswer.Container data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx">
        {answerList.map((answer, idx) => <QuizCard.SubjectiveAnswer.Item key={idx} text={answer} />)}
      </QuizCard.SubjectiveAnswer.Container>

      {hint?.value && <QuizCard.Hint type={hint.type as HintType} value={hint.value} />}
    </>;
};
const MatchAnswerPairItem = ({
  leftItem,
  rightItem
}: {
  leftItem?: Item;
  rightItem?: Item;
}) => {
  const renderItem = (item?: Item) => {
    const content = item?.richText || (item?.equation ? prepareTiptapHTML(`$${item.equation}$`) : item?.text);
    return <div className="flex min-w-[72px] flex-1 flex-col items-start justify-center gap-2xs px-3xs py-2xs text-left" data-sentry-component="renderItem" data-sentry-source-file="QuizItemCard.tsx">
        {item?.imageUrl && <QuizCard.Image.Container className="h-[96px] max-w-full">
            <QuizCard.Image.Thumbnail src={item.imageUrl} alt="" className="max-h-[96px] max-w-full object-contain" />
          </QuizCard.Image.Container>}
        {(content || !item?.imageUrl) && <StaticRichTextRenderer as="div" text={content || '—'} className="min-w-0 break-words text-heading-sm-bold text-default" />}
      </div>;
  };
  return <div className="flex min-w-0 flex-wrap items-start gap-x-xs gap-y-[2px]" data-sentry-component="MatchAnswerPairItem" data-sentry-source-file="QuizItemCard.tsx">
      {renderItem(leftItem)}
      <div className="flex min-w-[96px] flex-1 items-start gap-xs">
        <div className="flex shrink-0 pb-xs pt-sm">
          <ArrowRightIcon size="xs" fill={ICON_COLOR.assistive} data-sentry-element="ArrowRightIcon" data-sentry-source-file="QuizItemCard.tsx" />
        </div>
        {renderItem(rightItem)}
      </div>
    </div>;
};

/**
 * MatchContent - 매칭형 퀴즈 콘텐츠 렌더링
 */
const MatchContent = ({
  data,
  imageUrl,
  hint
}: {
  data: QuizItemData;
  imageUrl?: string;
  hint?: HintData;
}) => {
  const matchData = data.matchData;
  if (!matchData) return null;
  const questionText = matchData.questionRichText || matchData.question || '';
  const leftItemById = new Map(matchData.leftItems.map(item => [item.id, item]));
  const rightItemById = new Map(matchData.rightItems.map(item => [item.id, item]));
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <div className="flex flex-col gap-2xs pb-xs">
        {matchData.answerPairs.map(pair => <MatchAnswerPairItem key={`${pair.leftId}-${pair.rightId}`} leftItem={leftItemById.get(pair.leftId)} rightItem={rightItemById.get(pair.rightId)} />)}
      </div>

      {hint?.value && <QuizCard.Hint type={hint.type as HintType} value={hint.value} />}
    </>;
};

/**
 * EssayContent - 서술형 퀴즈 콘텐츠 렌더링 (정답 없음)
 */
const EssayContent = ({
  data,
  imageUrl,
  hint
}: {
  data: QuizItemData;
  imageUrl?: string;
  hint?: HintData;
}) => {
  const essayData = data.essayData;
  if (!essayData) return null;
  const questionText = essayData.questionRichText || essayData.question || '';
  return <>
      <QuizCard.QuestionText text={questionText} data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {imageUrl && <QuizCard.Image.QuestionImage src={imageUrl} alt="Question image" />}

      <QuizEssayAnswer.Placeholder data-sentry-element="unknown" data-sentry-source-file="QuizItemCard.tsx" />

      {hint?.value && <QuizCard.Hint type={hint.type as HintType} value={hint.value} />}
    </>;
};